import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { getListingEcolabels } from '../../config/ecolabels';
import { useValuePriorities } from '../../hooks/useValuePriorities';
import {
  Heading,
  EcolabelList,
  ValuePrioritiesMatch,
  ValuePrioritiesPanel,
} from '../../components';

import { ETHY_STANDARDS_URL } from './listingFieldGroups';
import css from './ListingPage.module.css';

// The three parts of every ecolabel explainer, in the order they're shown.
// Their copy lives in the translations as `EcolabelExplainer.<ecolabel key>.<part>`.
export const ECOLABEL_EXPLAINER_PARTS = ['meaning', 'evidence', 'limits'];

/**
 * "What this ecolabel means": one expandable row per ecolabel the product carries.
 *
 * The copy is ethy's own, based on the criteria at ethy.co.uk/our-standards but
 * written about the product rather than the brand. Each row says what the
 * ecolabel means, what evidence ethy checks, and what it doesn't cover — so a
 * shopper can see the limits of a claim as well as the claim itself.
 */
const EcolabelExplainers = props => {
  const { ecolabels } = props;
  return (
    <div className={css.ecolabelExplainers}>
      <Heading as="h3" rootClassName={css.ecolabelExplainersHeading}>
        <FormattedMessage id="ListingPage.ecolabelExplainersTitle" />
      </Heading>
      <ul className={css.ecolabelExplainerList}>
        {ecolabels.map(ecolabel => (
          <li key={ecolabel.key} className={css.ecolabelExplainer}>
            <details className={css.ecolabelExplainerDetails}>
              <summary className={css.ecolabelExplainerSummary}>
                <img
                  className={css.ecolabelExplainerBadge}
                  src={ecolabel.src}
                  alt=""
                  loading="lazy"
                />
                <span className={css.ecolabelExplainerLabel}>{ecolabel.label}</span>
              </summary>
              <dl className={css.ecolabelExplainerBody}>
                {ECOLABEL_EXPLAINER_PARTS.map(part => (
                  <div key={part} className={css.ecolabelExplainerPart}>
                    <dt className={css.ecolabelExplainerTerm}>
                      <FormattedMessage id={`ListingPage.ecolabelExplainer.${part}`} />
                    </dt>
                    <dd className={css.ecolabelExplainerText}>
                      <FormattedMessage id={`EcolabelExplainer.${ecolabel.key}.${part}`} />
                    </dd>
                  </div>
                ))}
              </dl>
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * The sustainability credentials of a product.
 *
 * Rather than listing every ecolabel with a tick or a cross — which reads as a
 * scorecard and makes a good product look half-empty — we show only the marks
 * the product has actually earned, as artwork, followed by what each one means
 * for this product and a route through to how ethy verifies them. When the
 * shopper has set priorities, the ones this product carries are picked out.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.publicData listing.attributes.publicData
 * @param {string} [props.heading] the hosted label of the ecolabels field
 * @returns {JSX.Element|null} null when the listing has no ecolabels
 */
const SectionSustainability = props => {
  const { publicData, heading } = props;

  const ecolabels = getListingEcolabels(publicData);
  const priorities = useValuePriorities();

  if (ecolabels.length === 0) {
    return null;
  }

  return (
    <section className={css.sectionSustainability}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        {heading || <FormattedMessage id="ListingPage.sustainabilityTitle" />}
      </Heading>

      <EcolabelList
        ecolabels={ecolabels}
        size="large"
        highlightKeys={priorities}
        className={css.sustainabilityBadges}
      />

      <div className={css.sustainabilityPriorities}>
        <ValuePrioritiesMatch
          priorities={priorities}
          ecolabelKeys={ecolabels.map(ecolabel => ecolabel.key)}
          showNoMatch
        />
        <ValuePrioritiesPanel showSummary={false} />
      </div>

      <EcolabelExplainers ecolabels={ecolabels} />

      <p className={css.sustainabilityStandards}>
        <FormattedMessage id="ListingPage.sustainabilityStandardsText" />{' '}
        <a
          className={css.sustainabilityStandardsLink}
          href={ETHY_STANDARDS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage id="ListingPage.sustainabilityStandardsLink" />
        </a>
      </p>
    </section>
  );
};

export default SectionSustainability;
